import { type Permission, type PermissionCategory } from '@/permissions/permissions.catalog';

export interface CategoryMeta {
  title: string;
  description: string;
}

export interface PermissionMeta {
  title: string;
  description: string;
  sensitive?: boolean;
}

export const CATEGORY_META: Record<PermissionCategory, CategoryMeta> = {
  vessel: {
    title: 'Vessel',
    description: 'Vessel information and operations.',
  },
  users: {
    title: 'User management',
    description: 'Crew and invitations',
  },
  menu: {
    title: 'Menu',
    description: 'Galley menus and change requests.',
  },
  activity: {
    title: 'Activity',
    description: 'Tasks, events and gallery.',
  },
  communication: {
    title: 'Communication',
    description: 'Rooms, messages and notifications.',
  },
  'service-provider': {
    title: 'Service Providers',
    description: 'External providers and ports.',
  },
  wallet: {
    title: 'Wallet',
    description: 'Transactions, invoices and quotes.',
  },
  document: {
    title: 'Library',
    description: 'Folders and file access.',
  },
  logbook: {
    title: 'Logbook',
    description: 'Daily logs and signatures.',
  },
};

export const PERMISSION_META: Partial<Record<Permission, PermissionMeta>> = {
  // vessel
  // GetVesselInfo: {
  //   title: 'View vessel details',
  //   description: 'See vessel specs, status and location',
  // },
  CreateVessel: {
    title: 'Add Vessel',
    description: 'Add a new vessel to the fleet.',
    // sensitive: true,
  },
  EditVesselInfo: {
    title: 'Edit Vessel Info',
    description: 'Update vessel details and photo.',
  },
  // TransferVessel: {
  //   title: 'Transfer ownership',
  //   description: 'Transfer vessel ownership to another user',
  //   sensitive: true,
  // },
  DeleteVessel: {
    title: 'Delete Vessel',
    description: 'Permanently delete the vessel.',
    sensitive: true,
  },
  Equipment: {
    title: 'Equipment',
    description: 'View and manage equipment aboard and its categories.',
  },
  ChefNote: {
    title: 'Chef Notes',
    description: 'View and manage chef notes about guests.',
  },
  GetMarinaInfo: {
    title: 'View Marinas',
    description: 'See the vessel’s berths, marina contacts and maps.',
  },
  MarinaBooking: {
    title: 'Marina Booking',
    description: 'Add, edit and remove berths, marina contacts and maps.',
  },
  Recipe: {
    title: 'Recipes',
    description: 'Manage galley recipes and recipe ingredients.',
  },
  EditSafetyInfo: {
    title: 'Manage Safety Checklists',
    description: 'Create, edit and complete safety checklists.',
  },
  ManagePermissions: {
    title: 'Manage Permissions',
    description: 'Open Roles & Permissions and change what each role and person can do.',
    sensitive: true,
  },

  // users
  CrewManagement: {
    title: 'Manage Crew',
    description: 'Add, invite, edit and remove crew members.',
  },
  InviteUser: {
    title: 'Invite Crew & Guests',
    description: 'Send crew and guest invitations to join the vessel.',
    sensitive: true,
  },
  JobAds: {
    title: 'Post vacancies',
    description: "Post, edit, close and remove the vessel's vacancies, and read who applied",
  },

  // menu
  GetMenuInfo: {
    title: 'View Menu',
    description: 'See the galley menu and meal plans.',
  },
  MenuChangeRequest: {
    title: 'Request Menu Changes',
    description: 'Leave comments on the menu.',
  },
  EditMenu: {
    title: 'Edit Menu',
    description: 'Manage menus, dishes and ingredients, and approve change requests.',
  },

  // activity
  Tasks: {
    title: 'View & Manage Tasks',
    description: 'View and manage tasks on the vessel.',
  },
  ExternalTasks: {
    title: 'View Tasks',
    description: 'See and change task status.',
  },
  // GetEventsInfo: {
  //   title: 'View events',
  //   description: 'View scheduled events',
  // },
  EventsActions: {
    title: 'Manage Events',
    description: 'Create, edit and delete events, attachments and participants.',
  },
  // GuestEvents: {
  //   title: 'Guest events',
  //   description: 'Respond to event invitations as a guest',
  // },
  InternalGallery: {
    title: 'View & Manage Vessel Gallery',
    description: 'Crew-only photos and media.',
  },
  ExternalGallery: {
    title: 'Shared Gallery',
    description: 'View shared gallery photos and media.',
  },
  Projects: {
    title: 'Projects',
    description: 'View and manage projects, their members and files',
  },
  Maintenance: {
    title: 'Maintenance',
    description: 'Create, schedule and track maintenance and recurring activities.',
  },

  // communication
  Folders: {
    title: 'Chat Folders',
    description: 'Organize conversations into folders.',
  },
  Notifications: {
    title: 'Notifications',
    description: 'Manage notifications, methods and categories.',
  },
  RoomsBasic: {
    title: 'Basic Rooms',
    description: 'View, pin, mute and organize chat rooms.',
  },
  RoomsExport: {
    title: 'Export Rooms',
    description: 'Export chat history and attachments.',
    // sensitive: true,
  },
  // RoomsAll: {
  //   title: 'All rooms',
  //   description: 'Create and manage all rooms and their members',
  //   // sensitive: true,
  // },
  RoomsInternal: {
    title: 'Internal Rooms',
    description: 'Create bid and email rooms.',
  },
  RoomsTemporary: {
    title: 'Temporary Rooms',
    description: 'Update temporary rooms.',
  },
  MessagesAll: {
    title: 'All Messages',
    description: 'Send, edit, pin and delete messages across rooms.',
    // sensitive: true,
  },
  CrewChatMessages: {
    title: 'Crew Chat',
    description: 'Pin broadcast messages in crew chat.',
  },

  // service-provider
  BasicServiceProvider: {
    title: 'View Service Providers',
    description: 'Browse providers, mark favorites and add notes.',
  },
  ServiceProviderInternal: {
    title: 'Manage Own Service Provider',
    description: 'Manage your own provider profile, locations and marina entries.',
  },
  // Ports: {
  //   title: 'Ports',
  //   description: 'View and manage port details',
  // },

  // wallet
  TransactionsBasic: {
    title: 'View Transactions',
    description: 'View and export transactions.',
  },
  Transactions: {
    title: 'View & Manage Transactions',
    description: 'View, create, import, edit and delete transactions.',
    // sensitive: true,
  },
  Invoices: {
    title: 'View & Manage Invoices',
    description: 'Create, import, export and manage invoices.',
  },
  Quotes: {
    title: 'View & Manage Quotes',
    description: 'Create, import, export and manage quotes.',
  },
  Payments: {
    title: 'View & Manage Payments',
    description: 'View and manage payments.',
  },
  VendorMappings: {
    title: 'Map Imported Transactions',
    description: 'Map imported transactions to service providers and categories.',
  },
  GetBudgets: {
    title: 'View Budgets',
    description: 'View category budgets.',
  },
  EditBudgets: {
    title: 'View & Manage Budgets',
    description: 'View, create, edit and delete category budgets.',
  },

  // document
  DocumentBasic: {
    title: 'Library',
    description: 'Upload, download, share and manage files.',
  },
  DocumentPrivate: {
    title: 'Manage File Permissions',
    description: 'Change file permissions.',
    sensitive: true,
  },
  OperationalContentFolderRead: {
    title: 'View Operational Content',
    description: 'View operational files.',
  },
  OperationalContentFolderWrite: {
    title: 'View & Manage Operational Content',
    description: 'View and edit operational files.',
  },
  SafetyContentFolderRead: {
    title: 'View Safety Content',
    description: 'View safety files.',
  },
  SafetyContentFolderWrite: {
    title: 'View & Manage Safety Content',
    description: 'View and edit safety files.',
  },
  GeneralInformationFolderRead: {
    title: 'View General Info',
    description: 'View general information files.',
  },
  GeneralInformationFolderWrite: {
    title: 'View & Manage General Info',
    description: 'View and edit general information files.',
  },
  FinancialFolder: {
    title: 'Financial Folder',
    description: 'Financial files and statements.',
    // sensitive: true,
  },
  MarinaFolder: {
    title: 'View Marina Folder',
    description: 'View marina files.',
  },
  SharedFolder: {
    title: 'View Shared Folder',
    description: 'View files shared across the vessel.',
  },

  // logbook
  DailyLog: {
    title: 'View & Manage Daily Log',
    description: 'View and manage daily logs.',
  },
  DailyLogSignCaptain: {
    title: 'Sign Daily Log (Captain)',
    description: 'Sign daily logs as captain.',
  },
  DailyLogSignMate: {
    title: 'Sign Daily Log (Mate)',
    description: 'Sign daily logs as mate.',
  },
  DailyLogDelete: {
    title: 'Delete Daily Log',
    description: 'Delete entries from the daily log.',
    sensitive: true,
  },
  DigitalNavigationLog: {
    title: 'View & Manage Navigation Log',
    description: 'View and manage daily navigation log entries.',
  },
  DigitalNavigationLogDelete: {
    title: 'Delete Navigation Log',
    description: 'Delete entries from the navigation log.',
    sensitive: true,
  },
  TrashLog: {
    title: 'View & Manage Trash Log',
    description: 'View and manage garbage record entries.',
  },
  TrashLogSign: {
    title: 'Sign Trash Log',
    description: 'Sign off garbage record entries.',
  },
  TrashLogDelete: {
    title: 'Delete Trash Log',
    description: 'Delete entries from the trash log.',
    sensitive: true,
  },
  SafetyLog: {
    title: 'View & Manage Safety Log',
    description: 'View and manage safety log entries.',
  },
  SafetyLogSign: {
    title: 'Sign Safety Log',
    description: 'Sign off safety log entries.',
  },
  SafetyLogDelete: {
    title: 'Delete Safety Log',
    description: 'Delete entries from the safety log.',
    sensitive: true,
  },
  OilLog: {
    title: 'View & Manage Oil Log',
    description: 'View and manage oil record book entries.',
  },
  OilLogDelete: {
    title: 'Delete Oil Log',
    description: 'Delete entries from the oil log.',
    sensitive: true,
  },
};

const splitCamelCase = (s: string): string => s.replace(/([A-Z])/g, ' $1').trim();

export const getCategoryMeta = (category: PermissionCategory): CategoryMeta =>
  CATEGORY_META[category];

export const getPermissionMeta = (name: string): PermissionMeta =>
  PERMISSION_META[name as Permission] ?? { title: splitCamelCase(name), description: '' };

export const hasPermissionMeta = (name: string): boolean =>
  PERMISSION_META[name as Permission] !== undefined;
